import React, { useState, useEffect } from 'react';
import ShieldIcon from '@mui/icons-material/Shield';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';
import SearchIcon from '@mui/icons-material/Search';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import SchoolIcon from '@mui/icons-material/School';
import BadgeIcon from '@mui/icons-material/Badge';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import GavelIcon from '@mui/icons-material/Gavel';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import css from './SchoolAgreementModal.module.css';

const TABS = [
  { key: 'all', label: 'Master Agreement', Icon: GavelIcon },
  { key: 'admin', label: 'School Proprietors & Admins', Icon: AccountBalanceIcon },
  { key: 'fees', label: 'Fees, Billing & Waiver Clawback', Icon: WarningAmberIcon },
  { key: 'teacher', label: 'Teachers & Examiners', Icon: BadgeIcon },
  { key: 'parent', label: 'Parents & Guardians', Icon: FamilyRestroomIcon },
  { key: 'student', label: 'Students & Learners', Icon: SchoolIcon },
  { key: 'privacy', label: 'NDPA Data & Safeguarding', Icon: ShieldIcon },
];

export default function SchoolAgreementModal({ open, initialRole = 'all', onClose, onAccept }) {
  const [activeTab, setActiveTab] = useState(initialRole || 'all');
  const [searchQuery, setSearchQuery] = useState('');

  // Sync role if prop updates
  useEffect(() => {
    if (initialRole) {
      setActiveTab(initialRole === 'fees' ? 'fees' : initialRole);
    }
  }, [initialRole]);

  // Handle ESC key press to close modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && open && onClose) {
        onClose();
      }
    };
    if (open) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [open, onClose]);

  const handlePrint = () => {
    const printContent = document.getElementById('bubble-ai-agreement-print-area');
    if (!printContent) {
      window.print();
      return;
    }

    const printWindow = window.open('', '_blank', 'width=900,height=700');
    if (!printWindow) {
      window.print();
      return;
    }

    const htmlContent = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Bubble Ai Schools — Master User Agreement & Safeguarding Policy</title>
  <style>
    @page {
      size: A4;
      margin: 18mm 15mm 18mm 15mm;
    }
    body {
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
      color: #1e293b;
      line-height: 1.6;
      font-size: 11pt;
      margin: 0;
      padding: 20px;
    }
    .print-header {
      border-bottom: 2px solid #0C1E28;
      padding-bottom: 16px;
      margin-bottom: 20px;
    }
    .print-logo-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 16px;
      margin-bottom: 14px;
    }
    .print-logo-item {
      display: flex;
      align-items: center;
      gap: 8px;
    }
    .print-logo-img {
      height: 38px;
      max-width: 140px;
      object-fit: contain;
    }
    .print-badge {
      display: inline-block;
      font-size: 8.5pt;
      font-weight: bold;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: #92400e;
      background: #fef3c7;
      border: 1px solid #fde68a;
      padding: 2px 8px;
      border-radius: 4px;
      margin-bottom: 6px;
    }
    .print-jurisdiction {
      font-size: 9pt;
      color: #64748b;
      margin-bottom: 6px;
    }
    .print-title {
      font-size: 15pt;
      font-weight: 800;
      color: #0C1E28;
      margin: 0 0 6px 0;
    }
    .print-meta {
      font-size: 9pt;
      color: #64748b;
    }
    .print-body {
      font-size: 10.5pt;
    }
    h3 {
      font-size: 12pt;
      font-weight: 800;
      color: #0f172a;
      margin: 20px 0 8px 0;
      border-bottom: 1px solid #e2e8f0;
      padding-bottom: 4px;
      page-break-after: avoid;
    }
    p {
      margin: 0 0 10px 0;
      text-align: justify;
    }
    strong {
      color: #0f172a;
    }
    ul {
      margin: 6px 0 12px 20px;
      padding: 0;
    }
    li {
      margin-bottom: 6px;
    }
    div[class*="waiverAlertBox"] {
      background: #fffbeb !important;
      border: 1.5px solid #d97706 !important;
      border-left: 6px solid #d97706 !important;
      padding: 12px 16px !important;
      margin: 16px 0 !important;
      border-radius: 6px !important;
      page-break-inside: avoid;
    }
    div[class*="legalIntroCard"] {
      background: #f0fdf4 !important;
      border: 1px solid #16a34a !important;
      border-left: 4px solid #16a34a !important;
      padding: 10px 14px !important;
      margin-bottom: 16px !important;
      border-radius: 6px !important;
      page-break-inside: avoid;
    }
    .print-footer {
      margin-top: 30px;
      padding-top: 12px;
      border-top: 1px solid #cbd5e1;
      font-size: 9pt;
      color: #64748b;
      display: flex;
      align-items: center;
      justify-content: space-between;
    }
  </style>
</head>
<body>
  <div class="print-header">
    <div class="print-logo-row">
      <div class="print-logo-item">
        <img src="/uploads/bubble-logo.png" alt="Bubble Ai" class="print-logo-img" onerror="this.style.display='none'" />
      </div>
      <div class="print-logo-item" style="text-align: right;">
        <img src="/uploads/blue-logo-blue.png" alt="Blue Spider Technologies Ltd" class="print-logo-img" onerror="this.style.display='none'" />
      </div>
    </div>
    <div class="print-badge">Official Legal Contract</div>
    <div class="print-jurisdiction">Federal Republic of Nigeria · NDPA 2023 · CAMA 2020 · Evidence Act 2011</div>
    <h1 class="print-title">Bubble Ai Schools — Master User Agreement & Safeguarding Policy</h1>
    <div class="print-meta">Operator: Blue Spider Technologies Ltd · Tab Scope: ${activeTab.toUpperCase()} · Date: ${new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</div>
  </div>
  <div class="print-body">
    ${printContent.innerHTML}
  </div>
  <div class="print-footer">
    <span>Bubble Ai Schools · Blue Spider Technologies Ltd</span>
    <span>Inquiries: support@bubble-ai.tech</span>
  </div>
</body>
</html>`;

    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();

    // Allow content and styles to render before triggering print
    setTimeout(() => {
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    }, 400);
  };

  const q = searchQuery.toLowerCase().trim();

  // Highlight or filter logic
  const shouldShowSection = (sectionId, tags = [], text = '') => {
    if (activeTab !== 'all') {
      if (!tags.includes(activeTab)) return false;
    }
    if (q) {
      return text.toLowerCase().includes(q) || tags.some(t => t.toLowerCase().includes(q));
    }
    return true;
  };

  if (!open) return null;

  return (
    <div
      className={css.modalOverlay}
      role="dialog"
      aria-modal="true"
      aria-labelledby="agreement-title"
      onClick={(e) => {
        if (e.target === e.currentTarget && onClose) onClose();
      }}
    >
      <div className={`${css.modalContainer} BubblePopIn`}>
        {/* Header */}
        <header className={css.modalHeader}>
          <div className={css.modalHeaderGlow} />
          
          <div className={css.headerTopRow}>
            <div className={css.headerLogos}>
              <img
                src="/uploads/bubble-logo.png"
                alt="Bubble Ai"
                className={css.headerLogoImg}
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
              <span className={css.logoDivider} />
              <img
                src="/uploads/blue-logo-blue.png"
                alt="Blue Spider Technologies Ltd"
                className={css.headerLogoImg}
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            </div>

            <div className={css.headerActions}>
              <button
                type="button"
                className={css.headerBtn}
                onClick={handlePrint}
                title="Print or Save as PDF"
              >
                <PrintIcon style={{ fontSize: '1rem' }} />
                <span className={css.btnTextHideMobile}>Print / PDF</span>
              </button>
              <button
                type="button"
                className={css.closeBtn}
                onClick={onClose}
                aria-label="Close Agreement Modal"
              >
                <CloseIcon style={{ fontSize: '1.2rem' }} />
              </button>
            </div>
          </div>

          <div className={css.headerMainRow}>
            <div className={css.headerIconWrap}>
              <GavelIcon style={{ fontSize: '1.35rem' }} />
            </div>
            <div className={css.headerTitleCol}>
              <div className={css.headerBadgeRow}>
                <span className={css.headerBadge}>Official Legal Contract</span>
                <span className={css.headerJurisdiction}>Federal Republic of Nigeria · NDPA 2023 · CAMA 2020</span>
              </div>
              <h2 className={css.headerTitle} id="agreement-title">
                Bubble Ai Schools — Master User Agreement & Safeguarding Policy
              </h2>
            </div>
          </div>
        </header>

        {/* Navigation & Search Bar */}
        <div className={css.navSection}>
          <div className={css.roleTabs}>
            {TABS.map(({ key, label, Icon }) => (
              <button
                key={key}
                type="button"
                className={`${css.roleTab} ${activeTab === key ? css.roleTabActive : ''}`}
                onClick={() => setActiveTab(key)}
              >
                <Icon style={{ fontSize: '0.95rem' }} />
                <span>{label}</span>
              </button>
            ))}
          </div>

          <div className={css.searchWrap}>
            <SearchIcon className={css.searchIcon} />
            <input
              type="text"
              className={css.searchInput}
              placeholder="Search legal clauses (e.g. waiver, NDPA, fees)..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        {/* Modal Scrollable Body */}
        <div className={css.modalBody} id="bubble-ai-agreement-print-area">
          {/* Legal Intro Banner */}
          <div className={css.legalIntroCard}>
            <VerifiedUserIcon style={{ color: '#16a34a', fontSize: '1.4rem', flexShrink: 0, marginTop: '2px' }} />
            <p className={css.legalIntroText}>
              <strong>Binding Legal Agreement:</strong> This Master User Agreement, Platform Terms of Service, and Safeguarding Policy
              constitutes a legally binding contract between <strong>Blue Spider Technologies Ltd</strong> (operators of <em>Bubble Ai</em> & <em>Bubble Ai Schools</em>)
              and all subscribing Educational Institutions, School Proprietors, Administrators, Academic Staff, Parents/Guardians, and Students.
              By accessing, registering, or using any part of Bubble Ai Schools, you unconditionally accept and agree to comply with all provisions herein.
            </p>
          </div>

          {/* CRITICAL CALLOUT: School Fee Collection, Deductions & Admin Waiver Clawback */}
          {(activeTab === 'all' || activeTab === 'admin' || activeTab === 'fees') && (
            <div className={css.waiverAlertBox}>
              <div className={css.waiverAlertHeader}>
                <WarningAmberIcon style={{ fontSize: '1.25rem' }} />
                <span>Critical Financial Clause: School Fees, Platform Deductions & Admin Waiver Clawback</span>
              </div>
              <p className={css.waiverAlertContent}>
                <strong>Attention School Owners, Principals & Bursars:</strong> Bubble Ai operates an automated financial collection
                and reconciliation infrastructure. Bubble Ai is contractually entitled to its software platform fee / technology service deduction
                on every invoice generated, scheduled, or managed on the platform.
                <br /><br />
                <strong>Where a School Administrator, Principal, Bursar, Proprietor, or authorized staff member waives, discounts, grants scholarships,
                settles offline, or marks any invoice as "Waived" or "Paid Offline"</strong> for any student/parent, <strong>Bubble Ai's platform fee
                remains fully earned, valid, due, and payable by the School</strong>. Bubble Ai reserves the immediate and irrevocable right
                to issue a direct commercial invoice/debit demand to the School for all platform fees due on such transactions, and to offset such
                sums against subsequent settlement batches or payouts.
              </p>
            </div>
          )}

          {/* ─── SECTION 1: PREAMBLE & STATUTORY FRAMEWORK ─── */}
          {shouldShowSection('sec1', ['all', 'admin', 'teacher', 'parent', 'student', 'privacy'], 'Preamble Parties Statutory Framework Blue Spider NDPA CAMA Nigeria') && (
            <section className={css.sectionBlock}>
              <h3 className={css.sectionHeading}>
                <span className={css.sectionNumber}>01</span>
                <span>Preamble, Parties & Statutory Authority</span>
              </h3>
              <p className={css.paragraph}>
                <strong>1.1 The Parties:</strong> This Agreement is made and entered into by and between <strong>Blue Spider Technologies Ltd</strong>,
                a technology company incorporated under the laws of the Federal Republic of Nigeria with RC registration ("Bubble Ai", "Platform Operator", "we", "us", or "our"),
                and the user accessing or operating the platform ("User", "Subscriber", or "Stakeholder"), categorized under one or more of the following designated roles:
              </p>
              <ul className={css.bulletList}>
                <li><span className={`${css.roleHighlightBadge} ${css.badgeAdmin}`}>School Admin</span> <strong>Educational Institutions & School Proprietors:</strong> Including School Owners, Governing Boards, Principals, Vice Principals, Bursars, and Sub-Admins.</li>
                <li><span className={`${css.roleHighlightBadge} ${css.badgeTeacher}`}>Teacher</span> <strong>Academic Instructors & Examiners:</strong> Subject specialists, class teachers, examination officers, and non-teaching personnel.</li>
                <li><span className={`${css.roleHighlightBadge} ${css.badgeParent}`}>Parent</span> <strong>Parents & Legal Guardians:</strong> Biological parents, court-appointed legal custodians, sponsors, and authorized fee payers.</li>
                <li><span className={`${css.roleHighlightBadge} ${css.badgeStudent}`}>Student</span> <strong>Enrolled Students & Learners:</strong> Pupils and candidates authorized by the school to undertake CBT exams, access AI tutoring, and view academic records.</li>
              </ul>
              <p className={css.paragraph}>
                <strong>1.2 Statutory Compliance:</strong> This Agreement is formulated and executed in strict alignment with the <em>Nigeria Data Protection Act 2023 (NDPA)</em>,
                <em>Companies and Allied Matters Act 2020 (CAMA)</em>, <em>Cybercrimes (Prohibition, Prevention, etc.) Act 2015 (as amended 2024)</em>,
                <em>Evidence Act 2011</em> (governing electronic signatures, digital audit trails, and automated transaction records), and
                <em>National Educational Research and Development Council (NERDC)</em> curriculum frameworks.
              </p>
            </section>
          )}

          {/* ─── SECTION 2: SCHOOL PROPRIETOR & INSTITUTIONAL OBLIGATIONS ─── */}
          {shouldShowSection('sec2', ['all', 'admin'], 'School Proprietor Institutional Obligations Authority Student Roster RBAC Sub-Admin') && (
            <section className={css.sectionBlock}>
              <h3 className={css.sectionHeading}>
                <span className={css.sectionNumber}>02</span>
                <span>Institutional Governance, Authority & School Roster Management</span>
              </h3>
              <p className={css.paragraph}>
                <strong>2.1 Legal Capacity & Warranties:</strong> The School Administrator / Proprietor warrants and represents that they possess full legal, corporate,
                and administrative authority to bind the educational institution to this Agreement, to deploy Bubble Ai across their student body, and to manage
                institutional data in compliance with Nigerian educational and data protection laws.
              </p>
              <p className={css.paragraph}>
                <strong>2.2 Accurate Onboarding & Roster Integrity:</strong> The School shall ensure that all student profiles, teacher accounts, parent contact details,
                fee structures, and class allocations uploaded onto the platform are accurate, lawful, and regularly updated. The School assumes full responsibility
                for all invitations issued to teachers, parents, and students.
              </p>
              <p className={css.paragraph}>
                <strong>2.3 Sub-Admin Delegation & Audit Responsibility:</strong> Where a School Administrator delegates granular administrative roles (such as Bursar,
                Exam Officer, Vice Principal, or Head of Department), the School remains strictly liable for all actions, deletions, modifications, and financial
                waivers executed under such sub-admin credentials. All administrative sessions are recorded in cryptographically protected, timestamped audit logs.
              </p>
            </section>
          )}

          {/* ─── SECTION 3: FINANCIAL TERMS, SETTLEMENTS & WAIVER CLAWBACK ─── */}
          {shouldShowSection('sec3', ['all', 'admin', 'fees', 'parent'], 'Financial Terms Automated Fee Settlement Deductions Waiver Clawback Flutterwave Offset Invoicing Default Interest') && (
            <section className={css.sectionBlock}>
              <h3 className={css.sectionHeading}>
                <span className={css.sectionNumber}>03</span>
                <span>Financial Settlement, Automated Deductions & The Admin Waiver Clawback Protocol</span>
              </h3>
              <p className={css.paragraph}>
                <strong>3.1 Integrated Cashless Fee Infrastructure:</strong> Bubble Ai provides a bank-grade, PCI-DSS Level 1 compliant financial processing infrastructure
                (in partnership with accredited Central Bank of Nigeria licensed payment gateways such as Flutterwave) enabling parents to remit school fees via Debit Cards,
                NIBSS Instant Bank Transfers, USSD, and Mobile Wallets.
              </p>
              <p className={css.paragraph}>
                <strong>3.2 Bubble Ai Platform Service Deductions:</strong> In consideration for providing real-time multi-channel billing, automated digital invoicing,
                instant SMS/Email/Push receipting, automated ledger reconciliation, and instant payout disbursement, Bubble Ai is contractually entitled to deduct its agreed
                technology platform fee and transaction processing commission from each school fee payment collected.
              </p>
              <div className={css.waiverAlertBox} style={{ margin: '14px 0' }}>
                <div className={css.waiverAlertHeader}>
                  <AccountBalanceIcon style={{ fontSize: '1.2rem' }} />
                  <span>3.3 Express Covenant on Admin Fee Waivers, Offline Payments & Direct Billing Clawback</span>
                </div>
                <p className={css.waiverAlertContent}>
                  <strong>(a) Entitlement to Earned Platform Fees:</strong> The School acknowledges that the moment an invoice is generated, published, or tracked within
                  the Bubble Ai ecosystem, technology and cloud resources have been allocated and computational processing has been rendered.
                  <br /><br />
                  <strong>(b) Waiver & Offline Discount Clawback:</strong> If a School Administrator, Principal, Bursar, or staff member grants an offline discount,
                  scholarship, bursary, accepts physical cash / offline direct bank transfer, or marks an invoice status as "Waived" or "Paid Offline" without routing the
                  transaction through Bubble Ai’s payment rail, <strong>Bubble Ai's platform service deduction that would have accrued on that invoice remains strictly due,
                  earned, and legally payable by the School</strong>.
                  <br /><br />
                  <strong>(c) Direct Institutional Invoicing:</strong> Bubble Ai reserves the immediate and unconditional right to generate and dispatch a direct corporate demand
                  invoice to the School Institution and its registered Proprietors for the cumulative sum of all platform fees due on waived or offline-marked invoices.
                  The School explicitly covenants to remit full payment for such direct invoices within seven (7) business days of issuance.
                  <br /><br />
                  <strong>(d) Right of Automatic Setoff & Payout Deduction:</strong> Bubble Ai is hereby irrevocably authorized by the School to automatically deduct and offset
                  any unpaid platform fees, invoice debts, or accrued arrears directly from subsequent automated payout batches, future parent fee settlements, or any escrow balance
                  held on behalf of the School.
                  <br /><br />
                  <strong>(e) Remedies for Financial Default:</strong> Where direct invoices remain unpaid after the 7-day grace period, Bubble Ai reserves the right to:
                  (i) Temporarily lock or downgrade administrative dashboard features, CBT exam publishing, and PDF broadsheet generation;
                  (ii) Charge statutory default interest at the rate of the Central Bank of Nigeria Monetary Policy Rate (MPR) plus two percent (+2%) per annum calculated daily until full settlement;
                  (iii) Institute formal civil debt recovery proceedings under the Summary Judgment procedure in the Federal High Court, with all legal costs, solicitors' fees, and debt recovery agency charges indemnified 100% by the defaulting School.
                </p>
              </div>
              <p className={css.paragraph}>
                <strong>3.4 Non-Refundability of Technology Subscription Fees:</strong> Standard and Premium school subscription plans (whether billed termly or annually)
                are non-refundable once activated, as server capacity, AI compute tokens, and cloud database instances are provisioned immediately upon institutional onboarding.
              </p>
              <p className={css.paragraph}>
                <strong>3.5 Chargeback & Fraud Indemnification:</strong> Where a parent or sponsor initiates a fraudulent payment reversal, chargeback, or dispute with their
                commercial bank after school fee payout has already been remitted to the School's designated bank account, the School shall cooperate with Bubble Ai to provide
                proof of service or, where the bank upholds the reversal, immediately refund or allow offset of the charged-back amount.
              </p>
            </section>
          )}

          {/* ─── SECTION 4: DATA SAFEGUARDING & NDPA 2023 COMPLIANCE ─── */}
          {shouldShowSection('sec4', ['all', 'privacy', 'admin', 'parent', 'student'], 'Data Safeguarding NDPA 2023 Privacy Child Minor Protection Encryption Controller Processor') && (
            <section className={css.sectionBlock}>
              <h3 className={css.sectionHeading}>
                <span className={css.sectionNumber}>04</span>
                <span>Data Safeguarding, Minor Protection & NDPA 2023 Compliance</span>
              </h3>
              <p className={css.paragraph}>
                <strong>4.1 Roles under the Nigeria Data Protection Act 2023:</strong> For the purposes of the NDPA 2023 and the Nigeria Data Protection Commission (NDPC)
                guidelines:
              </p>
              <ul className={css.bulletList}>
                <li>The <strong>School</strong> acts as the primary <strong>Data Controller</strong>, determining the legal basis and purposes for collecting student, teacher, and parent personal records.</li>
                <li><strong>Bubble Ai (Blue Spider Technologies Ltd)</strong> acts as the <strong>Data Processor</strong>, processing personal data strictly on the documented instructions of the School and to deliver the contracted software services.</li>
                <li>For cross-institutional security, fraud prevention, parental identity verification, and multi-tenant telemetry, Bubble Ai acts as a Joint Controller adhering to strict statutory privacy safeguards.</li>
              </ul>
              <p className={css.paragraph}>
                <strong>4.2 Absolute Protection of Minors (Child Safeguarding):</strong> Protecting children is our highest priority. Bubble Ai strictly enforces the following safeguards:
              </p>
              <ul className={css.bulletList}>
                <li><strong>Zero Commercialization:</strong> We NEVER sell, lease, monetize, or trade student personal records, continuous assessment scores, or behavioral logs to advertisers or third-party brokers.</li>
                <li><strong>Parental Consent:</strong> The School explicitly warrants that it holds valid parental/guardian consent or legal <em>in loco parentis</em> authority to onboard minor students onto the digital portal.</li>
                <li><strong>Age-Appropriate AI Guardrails:</strong> The AI tutoring copilot incorporates safety filters that prevent exposure to adult content, violence, hate speech, or harmful materials.</li>
              </ul>
              <p className={css.paragraph}>
                <strong>4.3 Cryptographic Security & Technical Measures:</strong> We employ enterprise-grade security architecture, including:
              </p>
              <ul className={css.bulletList}>
                <li><strong>256-Bit Encryption:</strong> Full AES-256 encryption for data at rest (including passwords, tokens, and bank details) and TLS 1.3 encryption for all data in transit.</li>
                <li><strong>Role-Based Access Control (RBAC):</strong> Strict cryptographic segregation ensuring teachers, parents, students, and admins only access records expressly permitted by their permission tier.</li>
                <li><strong>Automated Resilience & Backups:</strong> Geographically distributed redundant database backups with strict point-in-time disaster recovery protocols.</li>
              </ul>
              <p className={css.paragraph}>
                <strong>4.4 Data Subject Rights:</strong> Users have the right to request access, rectification, objection, or export of their data pursuant to the NDPA 2023 by submitting a verified request to our Data Protection Officer at <a href="mailto:support@bubble-ai.tech" style={{ color: '#3e8f93', fontWeight: 600 }}>support@bubble-ai.tech</a>.
              </p>
            </section>
          )}

          {/* ─── SECTION 5: TEACHERS, EXAMINERS & AI ACADEMIC ETHICS ─── */}
          {shouldShowSection('sec5', ['all', 'teacher', 'admin'], 'Teachers Examiners AI Academic Ethics Lesson Plans Question Bank Grading NERDC') && (
            <section className={css.sectionBlock}>
              <h3 className={css.sectionHeading}>
                <span className={css.sectionNumber}>05</span>
                <span>Teachers, Examiners & AI Academic Integrity</span>
              </h3>
              <p className={css.paragraph}>
                <strong>5.1 Ethical AI Deployment:</strong> Bubble Ai provides generative AI tools to assist educators with question bank synthesis, lesson plan drafting,
                and instant question diagnostic explanations. Educators acknowledge that generative AI acts as a pedagogical assistant (copilot); the final verification,
                factual accuracy, syllabus alignment, and pedagogical approval remain the professional responsibility of the certified teacher and school management.
              </p>
              <p className={css.paragraph}>
                <strong>5.2 Intellectual Property of Uploaded Exam Content:</strong> Teachers and Schools retain copyright over original exam questions, scheme-of-work
                curriculum materials, and lesson notes created by them. By uploading materials onto Bubble Ai, the School grants Bubble Ai a worldwide, royalty-free,
                non-exclusive license solely to host, process, format, and render such materials for institutional delivery.
              </p>
              <p className={css.paragraph}>
                <strong>5.3 Prohibition of Grade Tampering:</strong> Teachers and Examination Officers shall not artificially alter, inflate, forge, or tamper with continuous
                assessment (CA) or term exam results. All grade adjustments are irrevocably logged with the modifying user's timestamp, IP address, and previous values.
              </p>
            </section>
          )}

          {/* ─── SECTION 6: PARENTS & GUARDIANS COVENANTS ─── */}
          {shouldShowSection('sec6', ['all', 'parent'], 'Parents Guardians Covenants Fee Payments Attendance Child Progress Messaging') && (
            <section className={css.sectionBlock}>
              <h3 className={css.sectionHeading}>
                <span className={css.sectionNumber}>06</span>
                <span>Parents & Legal Guardians Terms of Engagement</span>
              </h3>
              <p className={css.paragraph}>
                <strong>6.1 Lawful Custodianship:</strong> Parents and guardians warrant that they are the biological parents, legal guardians, or authorized educational
                sponsors of the students linked to their family portal.
              </p>
              <p className={css.paragraph}>
                <strong>6.2 Digital Fee Payments & Electronic Receipts:</strong> School fees paid via the Bubble Ai Parent Portal generate an official electronic PDF receipt
                bearing a unique cryptographic reference. In accordance with Section 84 of the <em>Evidence Act 2011</em>, this electronic receipt constitutes prima facie
                conclusive proof of fee remittance.
              </p>
              <p className={css.paragraph}>
                <strong>6.3 Professional Communication Conduct:</strong> Parents agree to use the in-app School Messenger exclusively for constructive, professional communication
                regarding student academic welfare, attendance, and administrative inquiries. Defamatory statements, abusive language, or unauthorized mass solicitation
                are strictly prohibited and may result in immediate suspension of messaging privileges.
              </p>
            </section>
          )}

          {/* ─── SECTION 7: STUDENTS & CBT EXAM INTEGRITY ─── */}
          {shouldShowSection('sec7', ['all', 'student'], 'Students CBT Exam Integrity Anti-Cheating AI Tutor Conduct') && (
            <section className={css.sectionBlock}>
              <h3 className={css.sectionHeading}>
                <span className={css.sectionNumber}>07</span>
                <span>Student Code of Conduct & CBT Examination Integrity</span>
              </h3>
              <p className={css.paragraph}>
                <strong>7.1 Anti-Cheating & Exam Proctoring:</strong> When sitting for Continuous Assessment (CA) tests, mock examinations, or term exams on Bubble Ai,
                students undertake to maintain complete academic honesty. The platform automatically detects and logs suspicious behaviors, including:
              </p>
              <ul className={css.bulletList}>
                <li>Unauthorized tab-switching, browser minimizing, or background application execution;</li>
                <li>Simultaneous concurrent logins using the same student credentials from multiple devices;</li>
                <li>Use of external unauthorized AI prompts or automated scripting bots.</li>
              </ul>
              <p className={css.paragraph}>
                <strong>7.2 Examination Penalties:</strong> Verified attempts to compromise CBT exam integrity may result in immediate automatic exam submission,
                score cancellation, and formal disciplinary escalation to the School Principal.
              </p>
              <p className={css.paragraph}>
                <strong>7.3 Responsible AI Tutor Usage:</strong> Students agree to use the 24/7 AI tutor for genuine study aid, homework breakdown, and career exploration.
                Malicious attempts to prompt-inject or subvert AI safety filters will result in permanent AI tutor restriction.
              </p>
            </section>
          )}

          {/* ─── SECTION 8: SYSTEM INTEGRITY, PROHIBITED CONDUCT & PENALTIES ─── */}
          {shouldShowSection('sec8', ['all', 'admin', 'teacher', 'student'], 'System Integrity Prohibited Conduct Penalties Cybercrime Reverse Engineering Security') && (
            <section className={css.sectionBlock}>
              <h3 className={css.sectionHeading}>
                <span className={css.sectionNumber}>08</span>
                <span>Prohibited Conduct, System Integrity & Penalties</span>
              </h3>
              <p className={css.paragraph}>
                <strong>8.1 Prohibited Actions:</strong> Users shall NOT under any circumstances:
              </p>
              <ul className={css.bulletList}>
                <li>Reverse-engineer, decompile, disassemble, or derive the source code of Bubble Ai;</li>
                <li>Scrape, crawl, or harvest student records, staff payroll data, or question banks using automated bots;</li>
                <li>Share, resell, lease, or syndicate administrative, teacher, or student logins;</li>
                <li>Attempt to bypass paywalls, fee gating mechanisms, or subscription plan restrictions;</li>
                <li>Introduce viruses, Trojan horses, ransomware, or execute Denial of Service (DoS) attacks.</li>
              </ul>
              <p className={css.paragraph}>
                <strong>8.2 Criminal Prosecution under Cybercrimes Act:</strong> Any intentional unauthorized access, system interference, identity theft, or financial
                tampering constitutes a serious criminal offense under the <em>Cybercrimes (Prohibition, Prevention, etc.) Act 2015/2024</em>. Bubble Ai will immediately report
                offenders to the Nigeria Police Force Cybercrime Unit, EFCC, and State Ministries of Education for criminal prosecution alongside civil damages.
              </p>
            </section>
          )}

          {/* ─── SECTION 9: LIMITATION OF LIABILITY & INDEMNIFICATION ─── */}
          {shouldShowSection('sec9', ['all', 'admin'], 'Limitation of Liability Indemnification Force Majeure Warranties Cap') && (
            <section className={css.sectionBlock}>
              <h3 className={css.sectionHeading}>
                <span className={css.sectionNumber}>09</span>
                <span>Disclaimers, Limitation of Liability & Indemnity</span>
              </h3>
              <p className={css.paragraph}>
                <strong>9.1 "As-Is" Provision:</strong> While Bubble Ai guarantees an industry-leading 99.9% uptime and employs enterprise infrastructure, the platform is
                provided on an "as-is" and "as-available" basis. Bubble Ai disclaims all implied warranties of merchantability or fitness for a particular purpose.
              </p>
              <p className={css.paragraph}>
                <strong>9.2 Force Majeure:</strong> Bubble Ai shall not be held liable for temporary platform unavailability caused by events beyond its reasonable control,
                including nationwide telecommunication downtime, submarine internet cable cuts, national power grid failure, civil unrest, or statutory regulatory orders.
              </p>
              <p className={css.paragraph}>
                <strong>9.3 Monetary Liability Cap:</strong> To the maximum extent permitted by Nigerian law, Bubble Ai's aggregate financial liability arising out of or
                related to this Agreement or platform usage shall in no event exceed the total net technology platform fees actually received by Bubble Ai from the complaining
                entity during the three (3) months immediately preceding the occurrence of the claim.
              </p>
              <p className={css.paragraph}>
                <strong>9.4 Institutional Indemnification:</strong> The School agrees to indemnify, defend, and hold harmless Blue Spider Technologies Ltd, its directors,
                officers, employees, and software engineers from and against any third-party claims, liabilities, damages, fines, or legal costs arising from:
                (a) The School’s breach of NDPA student data consent requirements;
                (b) Internal employment, salary, or payroll disputes configured on the school dashboard;
                (c) Disputes between the School and parents regarding school fee policies, student expulsions, or grade allocations;
                (d) Copyright infringement claims relating to exam questions uploaded by school staff.
              </p>
            </section>
          )}

          {/* ─── SECTION 10: DISPUTE RESOLUTION & GOVERNING LAW ─── */}
          {shouldShowSection('sec10', ['all', 'admin', 'parent', 'teacher'], 'Dispute Resolution Governing Law Arbitration Abuja Nigeria AMA 2023') && (
            <section className={css.sectionBlock}>
              <h3 className={css.sectionHeading}>
                <span className={css.sectionNumber}>10</span>
                <span>Governing Law, Amicable Settlement & Binding Arbitration</span>
              </h3>
              <p className={css.paragraph}>
                <strong>10.1 Governing Law:</strong> This Agreement shall be governed by, construed, and enforced in accordance with the substantive laws of the
                <strong>Federal Republic of Nigeria</strong>.
              </p>
              <p className={css.paragraph}>
                <strong>10.2 Amicable Resolution:</strong> In the event of any controversy, claim, or dispute arising out of or relating to this Agreement or platform usage,
                the parties shall first make good-faith efforts to resolve the dispute amicably within fourteen (14) business days of written notice.
              </p>
              <p className={css.paragraph}>
                <strong>10.3 Binding Arbitration:</strong> If the dispute is not resolved amicably within the 14-day window, it shall be referred to and finally resolved
                by binding arbitration in accordance with the <em>Arbitration and Mediation Act 2023 (AMA)</em>. The arbitration tribunal shall consist of a single arbitrator
                appointed by the Chairman of the Chartered Institute of Arbitrators (UK, Nigeria Branch) or the Abuja Court of Arbitration. The seat of arbitration
                shall be Abuja, Nigeria, and proceedings shall be conducted in English. The arbitral award shall be final, binding, and enforceable in any court of competent jurisdiction.
              </p>
            </section>
          )}

          {/* ─── SECTION 11: MISCELLANEOUS & CONTACT ─── */}
          {shouldShowSection('sec11', ['all', 'admin', 'teacher', 'parent', 'student', 'privacy'], 'Miscellaneous Contact Legal Support Electronic Acceptance') && (
            <section className={css.sectionBlock}>
              <h3 className={css.sectionHeading}>
                <span className={css.sectionNumber}>11</span>
                <span>Electronic Acceptance, Severability & Contact Channels</span>
              </h3>
              <p className={css.paragraph}>
                <strong>11.1 Electronic Execution:</strong> In terms of the <em>Evidence Act 2011</em>, clicking "Sign In", "Get Started", "Accept Invite",
                submitting fee payments, or clicking "I Understand & Accept" constitutes a valid, legally enforceable electronic signature confirming that you have read,
                understood, and consented to be bound by this Agreement.
              </p>
              <p className={css.paragraph}>
                <strong>11.2 Severability:</strong> If any provision of this Agreement is held to be invalid or unenforceable by an arbitral tribunal or competent court,
                such provision shall be severed, and the remaining provisions shall continue in full legal force and effect.
              </p>
              <p className={css.paragraph}>
                <strong>11.3 Legal & Corporate Inquiries:</strong> For inquiries concerning this Agreement, institutional contracts, or data protection, please contact:
              </p>
              <ul className={css.bulletList}>
                <li><strong>Corporate Office:</strong> Blue Spider Technologies Ltd, Suite D19D & D20D, Platinum Mega Mall, Jahi, Abuja, Nigeria</li>
                <li><strong>Legal & Compliance Desk:</strong> <a href="mailto:support@bubble-ai.tech" style={{ color: '#3e8f93', fontWeight: 600 }}>support@bubble-ai.tech</a></li>
                <li><strong>Data Protection Officer (DPO):</strong> <a href="mailto:support@bubble-ai.tech" style={{ color: '#3e8f93', fontWeight: 600 }}>support@bubble-ai.tech</a></li>
                <li><strong>School Institutional Desk:</strong> <a href="mailto:support@bubble-ai.tech" style={{ color: '#3e8f93', fontWeight: 600 }}>hello@bubble-ai.tech</a></li>
              </ul>
            </section>
          )}
        </div>

        {/* Modal Footer */}
        <footer className={css.modalFooter}>
          <div className={css.footerNote}>
            <CheckCircleOutlineIcon style={{ color: '#16a34a', fontSize: '1.05rem' }} />
            <span>
              By using Bubble Ai Schools, you confirm compliance with <strong>NDPA 2023</strong> and the <strong>Master User Agreement</strong>.
            </span>
          </div>

          <div className={css.footerActions}>
            <button
              type="button"
              className={css.btnDismiss}
              onClick={onClose}
            >
              Close
            </button>
            <button
              type="button"
              className={css.btnAccept}
              onClick={() => {
                if (onAccept) onAccept();
                if (onClose) onClose();
              }}
            >
              <VerifiedUserIcon style={{ fontSize: '1.05rem' }} />
              <span>I Understand & Accept</span>
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}

